import React, { useEffect, useState, useMemo, useCallback } from 'react';
import FetchData from '../../fetch-api/Apifetch';
import { LIST_DEPARTMENT, LIST_EMPLOYEE_ACTIVITY, LIST_REPORT, LIST_SUBDEPARTMENT } from '../../utils/ApiRoute';
import { Shimmer } from '../../common/Shimmer';
import { CSVLink } from 'react-csv';
import PagesHeadings from '../../common/PagesHeadings';
import { EncryptData, FILTER_DAYS } from '../../utils/Constants';
import { useTranslation } from 'react-i18next';
import { DeptDropdown, EmpDropdown, SubDeptDropdown } from '../Keystroke/EmpDropdown';
import { NOData } from '../../utils/Function';
import debounce from 'lodash.debounce';
import { useGlobalContext } from '../../context/GlobalContext';

const EmployeeActivity = ({ filters, setFilters }) => {
    const { selectedEmpId, empList, deviceList, selectedDeviceId, user } = useGlobalContext();
    const { t } = useTranslation();
    const currentDate = new Date().toISOString().split('T')[0];
    const [crypto] = useState(JSON.parse(localStorage.getItem('crypto_key')));
    const [EmpReport, setEmpReport] = useState([]);
    const [Attendence, setAttendence] = useState([]);
    const [loading, setloading] = useState(true);
    const [filterOption, setFilterOption] = useState("");
    const [reportEmpId, setReportEmpId] = useState(selectedEmpId || "");
    const [reportDeviceId, setReportDeviceId] = useState(selectedDeviceId || "");
    const [filterid, setfilterid] = useState(selectedEmpId);
    const [showCustomDates, setShowCustomDates] = useState(false);
    const [date, setDate] = useState(currentDate);
    const [FilterData, setFilterData] = useState({ from: '', to: '' });
    const [selectedEmployee, setSelectedEmployee] = useState({});
    const [paginationInfo, setPaginationInfo] = useState({
        total_count: 0,
        page: 1,
        limit: 10,
        next: null,
        previous: null,
    });

    const [fromDate, setFromDate] = useState(currentDate);
    const [toDate, setToDate] = useState(currentDate);

    const [DepartmentID, setDepartmentId] = useState();
    const [SubDepartmentID, setSubDepartmentId] = useState();
    const [DepartmentList, setDepartmentList] = useState([]);
    const [SubDepartmentList, setSubDepartmentList] = useState([]);

    const headers = useMemo(() => [
        { label: "Date", key: "date" },
        { label: "IN Time", key: "inTime" },
        { label: "OUT Time", key: "outTime" },
        { label: "Hours spend", key: "hourspend" },
        { label: "Average work", key: "workhour" },
        { label: "Average Break", key: "manueltime" },
        { label: "Average Idle", key: "idletime" }
    ], []);

    useEffect(() => {
        ListDepartment();
        ListSubDepartment();
    }, [])

    // Department
    const ListDepartment = async () => {
        try {
            const res = await FetchData(LIST_DEPARTMENT, 'GET', null, true, false)
            setDepartmentList(res.data)
        } catch (err) {
            console.error(err);
        }
    }

    const ListSubDepartment = async () => {
        try {
            const res = await FetchData(LIST_SUBDEPARTMENT, 'GET', null, true, false)
            setSubDepartmentList(res.data)
        } catch (err) {
            console.log(err);
        }
    }

    // Memoized function to fetch employee activity data
    const fetchEmployeeActivity = useCallback(async (id = "", dev_id = "", page = 1, limit = 10, from = "", to = "", range = "", depart_id = "", subdept_id = "") => {
        setloading(true);
        try {
            const empParam = reportEmpId ? reportEmpId : id;
            const devParam = reportDeviceId ? reportDeviceId : dev_id;
            let url = `${LIST_EMPLOYEE_ACTIVITY}?page=${page}&limit=${limit}&emp_id=${empParam}&from_date=${from}&to_date=${to}&date_range=${range}&&subdept_id=${subdept_id}&depart_id=${depart_id}`;
            if (devParam && devParam !== "0") {
                url += `&device_id=${devParam}`;
            }
            const res = await FetchData(
                url,
                'GET',
                null,
                true,
                false
            );
            checkActivityTime(res);
            if (res.status) {
                setPaginationInfo({
                    total_count: res.pagination.total_count,
                    page: res.pagination.page,
                    limit: res.pagination.limit,
                    next: res.pagination.next,
                    previous: res.pagination.previous,
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setloading(false);
        }
    }, [reportEmpId, reportDeviceId]);

    // Memoized function to check activity time
    const checkActivityTime = useCallback((data) => {
        const attendence = data?.data?.map(item => ({
            ...item.data,
            employee_name: item.employee_name,
            schedule_end: item.schedule_end,
            schedule_start: item.schedule_start,
            manual_time: item.manual_time,
            total_hours_per_day: item.total_hours_per_day
        })) || [];
        setAttendence(attendence);
    }, []);

    // Memoized function to handle date input changes
    const handledateinput = useCallback((e) => {
        const { name, value } = e.target;
        setFilterData(prev => ({ ...prev, [name]: value }));
    }, []);

    // Debounced filter data function
    const debouncedFilterData = useMemo(() => debounce(async (range = 'custom') => {
        // await fetchEmployeeActivity(selectedEmpId, paginationInfo.page, paginationInfo.limit, FilterData.from, FilterData.to, range);
    }, 300), [selectedEmpId, paginationInfo, FilterData]);

    // Memoized function to handle filter changes
    const handleFilterChange = useCallback((option) => {
        setFilterOption(option);
        setShowCustomDates(option === 'custom');
        if (option !== "custom") {
            debouncedFilterData(option);
            setFilterData({ from: "", to: "" });
        }
    }, [debouncedFilterData]);

    // Sync with global selection
    useEffect(() => {
        if (selectedEmpId) {
            setfilterid(selectedEmpId);
            const employee = EmpReport.find(emp => emp.emp_id === selectedEmpId);
            setSelectedEmployee(employee || {});
        } else {
            setfilterid("");
            setSelectedEmployee({});
        }
    }, [selectedEmpId, EmpReport]);

    // Memoized function to generate CSV data
    const generateCustomCSVData = useCallback((data, employee) => {
        const empDetails = `Employee ID: ${employee.emp_id || 'N/A'}\nName: ${employee.name || 'N/A'}\nPhone: ${employee.phone || 'N/A'}\nEmail: ${employee.email || 'N/A'}\n\n`;
        const csvRows = data.map(item => [
            item.date,
            item.inTime,
            item.outTime,
            item.hourspend,
            item.workhour,
            item.manueltime,
            item.idletime
        ].join(',')).join('\n');
        return `${empDetails}${headers.map(header => header.label).join(',')}\n${csvRows}`;
    }, [headers]);

    // Memoized function to handle page changes
    const handlePageChange = useCallback((newPage) => {
        setPaginationInfo(prev => ({ ...prev, page: newPage }));
    }, []);

    // Memoized function to calculate overtime
    const checkOvertime = useCallback((totalHoursPerDay, overallActiveTime) => {
        const timeToSeconds = (timeStr) => {
            const [hours = 0, minutes = 0, seconds = 0] = timeStr?.split(':').map(Number);
            return hours * 3600 + minutes * 60 + seconds;
        };
        const secondsToTime = (seconds) => {
            const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
            const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
            const secs = String(seconds % 60).padStart(2, '0');
            return `${hrs}:${mins}:${secs}`;
        };
        const totalSecondsPerDay = timeToSeconds(totalHoursPerDay);
        const activeSeconds = timeToSeconds(overallActiveTime);
        const overtimeSeconds = activeSeconds - totalSecondsPerDay;
        return overtimeSeconds > 0 ? secondsToTime(overtimeSeconds) : "00:00:00";
    }, []);

    // Fetch data on component mount and when pagination changes
    useEffect(() => {
        if (reportEmpId) {
            fetchEmployeeActivity(reportEmpId, reportDeviceId, paginationInfo.page, paginationInfo.limit, FilterData.from, FilterData.to, filterOption);
        } else {
            fetchEmployeeActivity(reportEmpId, reportDeviceId, paginationInfo.page, paginationInfo.limit, '', '', date, DepartmentID, SubDepartmentID);
        }
    }, [reportEmpId, reportDeviceId, paginationInfo.page, paginationInfo.limit, FilterData, filterOption, fetchEmployeeActivity, date, DepartmentID, SubDepartmentID]);

    // Fetch reporting employees on component mount
    useEffect(() => {
        const fetchReportingEmp = async () => {
            try {
                const res = await FetchData(`${LIST_REPORT}?emp_id=${EncryptData(user.emp_id, crypto)}&type=${user.type}`, 'GET', null, true, false);
                setEmpReport([{ id: "", name: "All Employees", emp_id: "" }, ...res.data.reporting_employees]);
            } catch (err) {
                console.error(err);
            }
        };
        fetchReportingEmp();
    }, [user]);

    const timeToSeconds = (timeStr) => {
        if (!timeStr) return 0;
        const [hours = 0, minutes = 0, seconds = 0] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    };

    const secondsToTime = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return [hours, minutes, seconds].map(val => String(val).padStart(2, '0')).join(':');
    };

    const subtractTimes = (time1, time2) => {
        const seconds1 = timeToSeconds(time1);
        const seconds2 = timeToSeconds(time2);
        const differenceInSeconds = seconds1 - seconds2;
        return secondsToTime(Math.max(differenceInSeconds, 0));
    };

    const sumTime = (times) => {
        let totalSeconds = times?.reduce((acc, time) => {
            const [h, m, s] = time.split(':').map(Number);
            return acc + h * 3600 + m * 60 + s;
        }, 0);
        return secondsToTime(totalSeconds);
    };

    const PaginationIndex1 = () => {
        const totalPages = Math.ceil(paginationInfo?.total_count / paginationInfo?.limit);
        const currentPage = paginationInfo?.page;

        const getPageNumbers = () => {
            const pageNumbers = [];
            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                if (currentPage > 3) {
                    pageNumbers.push("...");
                }
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                for (let i = start; i <= end; i++) {
                    pageNumbers.push(i);
                }
                if (currentPage < totalPages - 2) {
                    pageNumbers.push("...");
                }
                pageNumbers.push(totalPages);
            }
            return pageNumbers;
        };

        return (
            paginationInfo && (
                <div className='flex-column'>
                    <nav aria-label="Page navigation example">
                        <ul className="pagination" style={{ marginLeft: "10px" }}>
                            <li
                                className={`page-item ${paginationInfo?.previous ? "" : "disabled"}`}
                            >
                                <a
                                    className={`page-link ${paginationInfo?.previous ? "colored-button" : ""}`}
                                    href="#"
                                    aria-label="Previous"
                                    onClick={() => paginationInfo?.previous && handlePageChange(paginationInfo.page - 1)}
                                // onClick={() => paginationInfo?.previous && PreviousPage()}
                                >
                                    <span aria-hidden="true">Previous</span>
                                </a>
                            </li>
                            {getPageNumbers().map((page, index) => (
                                <li
                                    key={index}
                                    className={`page-item ${paginationInfo?.page === page ? "active" : ""
                                        }`}
                                >
                                    {page === "..." ? (
                                        <span className="page-link">...</span>
                                    ) : (
                                        <a
                                            className="page-link"
                                            href="#"
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </a>
                                    )}
                                </li>
                            ))}
                            <li
                                className={`page-item ${paginationInfo?.next ? "" : "disabled"}`}
                            >
                                <a
                                    className={`page-link ${paginationInfo?.next ? "colored-button" : ""}`}
                                    href="#"
                                    aria-label="Next"
                                    onClick={() => paginationInfo?.next && handlePageChange(paginationInfo.page + 1)}
                                >
                                    <span aria-hidden="true">Next</span>
                                </a>
                            </li>
                        </ul>
                    </nav>
                    <div className="pagination" style={{ marginLeft: "17px" }}>
                        <p>Total Records: {paginationInfo?.total_count}</p>
                        <p style={{ paddingLeft: "10px" }}>
                            Total Pages: {totalPages}
                        </p>
                    </div>
                </div>
            )
        );
    };

    function compareTimes(totalHours, workTime) {
        const timeToMinutes = (time) => {
            if (!time) return 0;
            const parts = time.split(":");
            const hours = parseInt(parts[0]) || 0;
            const minutes = parseInt(parts[1]) || 0;
            const seconds = parseInt(parts[2]) || 0;
            return hours * 60 + minutes + seconds / 60;
        };

        const totalMinutesPerDay = timeToMinutes(totalHours);
        const workMinutes = timeToMinutes(workTime);

        if (Math.abs(totalMinutesPerDay - workMinutes) < 0.1) {
            return "0"; // White (Equal)
        } else if (workMinutes > totalMinutesPerDay) {
            return "2"; // Light Green (Over)
        } else {
            return "1"; // Light Red (Less)
        }
    }

    return (
        <>
            <PagesHeadings PageHeader={t("Employee")} insidehead={t("dashboard")} />
            {loading ? (
                <Shimmer />
            ) : (
                <div className="md:col-span-12 lg:col-span-7 xl:col-span-7 col-span-12">
                    <div className="box !p-[20px]">
                        <div className="box-header !border-b-0 !p-0 flex flex-wrap items-center justify-between gap-3">
                            <h4 className="box-title p-2 mb-0">{t("Employee_Activity")}</h4>
                            <div className="flex flex-wrap items-center gap-2">

                                {!reportEmpId && (
                                    <>
                                        <DeptDropdown
                                            placeholder={DepartmentID ? DepartmentList.find(item => item.id === DepartmentID)?.title : 'Select Department'}
                                            data={DepartmentList}
                                            handleChange={(e) => { setDepartmentId(e) }}
                                        />
                                        <SubDeptDropdown
                                            placeholder={SubDepartmentID ? SubDepartmentList.find(item => item.SubDepartmentId === SubDepartmentID)?.SubDepartmentTitle : 'Select Sub Department'}
                                            data={SubDepartmentList}
                                            handleChange={(e) => { setSubDepartmentId(e) }}
                                        />
                                        <div className="flex items-center">
                                            <input
                                                type="date"
                                                name="date"
                                                style={{ height: '2.4rem' }}
                                                className="form-input rounded-md border-gray-300"
                                                value={date || new Date().toISOString().split('T')[0]}
                                                onChange={(e) => { setDate(e.target.value) }}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="flex items-center">
                                    <select
                                        className="form-select rounded-md border-gray-300"
                                        style={{ height: '2.4rem', minWidth: '10rem' }}
                                        value={reportEmpId}
                                        onChange={(e) => setReportEmpId(e.target.value)}
                                    >
                                        <option value="">All Employees</option>
                                        {empList && empList.map(emp => (
                                            <option key={emp.emp_id} value={emp.emp_id}>
                                                {emp.name} ({emp.emp_id})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center">
                                    <select
                                        className="form-select rounded-md border-gray-300"
                                        style={{ height: '2.4rem', minWidth: '10rem' }}
                                        value={reportDeviceId}
                                        onChange={(e) => setReportDeviceId(e.target.value)}
                                        disabled={!reportEmpId || deviceList.length === 0}
                                    >
                                        <option value="0">All Devices</option>
                                        {deviceList && deviceList.map((dev) => (
                                            <option key={dev.Value} value={dev.Value}>
                                                {dev.Text}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {reportEmpId && (
                                    <div className="dropdown">
                                        <button className="btn btn-drop-down dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            {filterOption || t('Selectfilter')}
                                        </button>
                                        <ul className="dropdown-menu">
                                            {FILTER_DAYS.map((option) => (
                                                <li key={option.value}>
                                                    <a className="dropdown-item" href="#" onClick={() => handleFilterChange(option.value)}>
                                                        {option.label}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <CSVLink
                                    filename={'userdata.csv'}
                                    className="btn btn-exp px-3 flex items-center justify-center font-medium"
                                    style={{ height: '2.4rem', fontSize: '0.875rem' }}
                                    data={generateCustomCSVData(Attendence, selectedEmployee)}
                                >
                                    {t('Export')}
                                </CSVLink>
                            </div>

                            {reportEmpId && showCustomDates && (
                                <div className="w-full flex flex-wrap items-end justify-end gap-3 mt-2">
                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold mb-1 ml-1 text-gray-500">From Date</label>
                                        <input
                                            type="date"
                                            name="from"
                                            style={{ height: '2.4rem' }}
                                            className="form-input rounded-md border-gray-300"
                                            value={fromDate}
                                            onChange={(e) => { setFromDate(e.target.value) }}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold mb-1 ml-1 text-gray-500">To Date</label>
                                        <input
                                            type="date"
                                            name="to"
                                            style={{ height: '2.4rem' }}
                                            className="form-input rounded-md border-gray-300"
                                            value={toDate}
                                            min={fromDate || undefined}
                                            max={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => { setToDate(e.target.value) }}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-wfm px-4 h-[2.4rem] rounded-md font-semibold"
                                        onClick={() => {
                                            setFilterData(prev => ({
                                                ...prev,
                                                from: fromDate,
                                                to: toDate,
                                            }));
                                        }}
                                    >
                                        Apply Filter
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card my-2">
                            <div className='table-responsive'>
                                <table className="table table-bordered table-hover table-sm text-center whitespace-nowrap min-w-full mb-0">
                                    <thead className='bg-primary-subtle'>
                                        <tr className='border-0 bg-primary-subtle'>
                                            <th scope="col">{t('Numbers')}</th>
                                            {reportEmpId ?
                                                <th scope="col">{t('Dates')}</th>
                                                :
                                                <th scope="col">{t('Employee_Name')}</th>
                                            }
                                            <th scope="col">{t('Overall Working time')}</th>
                                            <th scope="col">{t('Overall Active time')}</th>
                                            <th scope="col">{t('IdleTime')}</th>
                                            <th scope="col">{t('BreakTime')}</th>
                                            <th scope="col">{t('OverTime')}</th>
                                            <th scope="col">{t('ManualTime')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Attendence.map((item, index) => (
                                            <tr className={
                                                compareTimes(item.total_hours_per_day ? item.total_hours_per_day + ":00" : "07:00:00", item.Work_time) == "0" ? 'white-bg' :
                                                    compareTimes(item.total_hours_per_day ? item.total_hours_per_day + ":00" : "07:00:00", item.Work_time) == "2" ? 'light-green-bg' : 'light-red-bg'
                                            } key={index}>
                                                <td>{(paginationInfo.page - 1) * 10 + index + 1}</td>
                                                {reportEmpId ?
                                                    <td>{item.active_time_array?.[0]?.system_on?.split(' ')[0]}</td>
                                                    :
                                                    <td>{item.employee_name}</td>}
                                                <td>{item.Work_time}</td>
                                                <td>{item.overall_activetime}</td>
                                                <td>{item.Idle_time}</td>
                                                <td>{item.Break_time}</td>
                                                <td>{checkOvertime(item.total_hours_per_day ? item.total_hours_per_day + ":00" : "07:00:00", item.Work_time || "00:00:00")}</td>
                                                <td>{item.manual_time || "00:00:00"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {Attendence.length === 0 && <NOData />}
                            </div>
                            <PaginationIndex1
                                paginationInfo={paginationInfo}
                                handlePageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default React.memo(EmployeeActivity);